import { useTranslation } from 'react-i18next';
import type { MembershipTypeV2 } from '@/api/generated/types';
import { toneOf, type Option, type Tone } from '@/ui';
import { ACCESS_STATUS, CUSTOMER_STATUS, EMAIL_STATUS, MEMBERSHIP_TYPES, NOTIFICATION_CHANNEL, PASS_STATUS, TRANSACTION_TYPE, VIOLATION_STATUS } from './model';

type Coded = Record<string | number, { text: string; color: string }>;

/**
 * Display names for the customer codes in the active language. Codes and colours stay in
 * model.ts; the words come from the `customers` translations (English text in model.ts is the
 * fallback for a code the translations do not know yet).
 */
export function useCustomerLabels() {
  const { t } = useTranslation('customers');
  const words = (key: 'codes.status' | 'codes.emailStatus' | 'codes.transactionTypes' | 'codes.membershipTypes' | 'codes.passStatus' | 'codes.accessStatus' | 'codes.violationStatus' | 'codes.channels') =>
    t(key, { returnObjects: true }) as Record<string, string>;

  const badge = (map: Coded, names: Record<string, string>, code: string | number | null | undefined): { label: string; tone: Tone } | undefined => {
    const entry = code == null ? undefined : map[code];
    return entry && { label: names[String(code)] ?? entry.text, tone: toneOf(entry.color) };
  };
  const options = <V extends string | number>(codes: readonly V[], names: Record<string, string>, fallback: (code: V) => string): Option<V>[] =>
    codes.map((value) => ({ value, label: names[String(value)] ?? fallback(value) }));

  const status = words('codes.status');
  const emailStatus = words('codes.emailStatus');
  const transactionTypes = words('codes.transactionTypes');
  const membershipTypes = words('codes.membershipTypes');
  const passStatus = words('codes.passStatus');
  const accessStatus = words('codes.accessStatus');
  const violationStatus = words('codes.violationStatus');
  const channels = words('codes.channels');

  const numericKeys = (map: object) => Object.keys(map).map(Number);

  return {
    /** Customer account status as a badge; undefined for unknown codes. */
    status: (code: number | null | undefined) => badge(CUSTOMER_STATUS, status, code),
    statusOptions: options(numericKeys(CUSTOMER_STATUS), status, (c) => CUSTOMER_STATUS[c]?.text ?? String(c)),
    emailStatus: (code: number | null | undefined) => (code == null ? undefined : (emailStatus[String(code)] ?? EMAIL_STATUS[code] ?? String(code))),
    emailStatusOptions: options(numericKeys(EMAIL_STATUS), emailStatus, (c) => EMAIL_STATUS[c] ?? String(c)),
    transactionType: (code: number | null | undefined) =>
      code == null ? '—' : (transactionTypes[String(code)] ?? TRANSACTION_TYPE[code] ?? t('codes.transactionTypeFallback', { code })),
    transactionTypeOptions: options(numericKeys(TRANSACTION_TYPE), transactionTypes, (c) => TRANSACTION_TYPE[c] ?? String(c)),
    membershipType: (code: MembershipTypeV2 | null | undefined) => (code ? (membershipTypes[code] ?? code) : '—'),
    membershipTypeOptions: options<MembershipTypeV2>(
      MEMBERSHIP_TYPES.map((m) => m.value),
      membershipTypes,
      (v) => MEMBERSHIP_TYPES.find((m) => m.value === v)?.label ?? v,
    ),
    /** Ride pass status as a badge; undefined for unknown codes. */
    passStatus: (code: string | null | undefined) => badge(PASS_STATUS, passStatus, code),
    passStatusOptions: options(Object.keys(PASS_STATUS), passStatus, (c) => PASS_STATUS[c]?.text ?? c),
    accessStatus: (code: string | null | undefined) => (code ? (accessStatus[code.toLowerCase()] ?? code) : '—'),
    accessStatusOptions: options<string>([...ACCESS_STATUS], accessStatus, (c) => c),
    /** Violation review status as a badge; undefined for unknown codes. */
    violationStatus: (code: number | null | undefined) => badge(VIOLATION_STATUS, violationStatus, code),
    violationStatusOptions: options(numericKeys(VIOLATION_STATUS), violationStatus, (c) => VIOLATION_STATUS[c]?.text ?? String(c)),
    channel: (code: number | null | undefined) => (code == null ? '—' : (channels[String(code)] ?? NOTIFICATION_CHANNEL[code] ?? String(code))),
    channelOptions: options(numericKeys(NOTIFICATION_CHANNEL), channels, (c) => NOTIFICATION_CHANNEL[c] ?? String(c)),
  };
}

export type CustomerLabels = ReturnType<typeof useCustomerLabels>;
